import CommingImg from '@/assets/images/comming.png';
import { useToast } from '@/contexts/ToastContext';
import { verifyShopifyStore } from '@/utils';
import { authService } from '@common/api/authService';
import { Button } from '@common/components/Button';
import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import { ShopifyUrlInput } from '../ConnectShopify';
import AnimatedElement from './AnimatedElement';
import styles from './styles.module.scss';
import { FormProps } from './types';
import TypewriterText from './TypewriterText';

const PlatformOptions = [{
    label: 'Shopify',
    icon: 'shopify',
    validate: verifyShopifyStore
}, {
    label: 'Amazon',
    icon: 'amazon',
    disabled: true,
    validate: async () => { return ''}
}, {
    label: 'eBay',
    icon: 'ebay',
    disabled: true,
    validate: async () => { return ''}
}, {
    label: 'Etsy',
    icon: 'Etsy',
    disabled: true,
    validate: async () => { return ''}
}, {
    label: 'Salesforce',
    icon: 'Salesforce',
    disabled: true,
    validate: async () => { return ''}
}, {
    label: 'WooCommerce',
    icon: 'WooCommerce',
    disabled: true,
    validate: async () => { return ''}
}, {
    label: 'BigCommerce',
    icon: 'BigCommerce',
    disabled: true,
    validate: async () => { return ''}
}, {
    label: 'Magento',
    icon: 'magento',
    disabled: true,
    validate: async () => { return ''}
}, {
    label: 'Walmart',
    icon: 'Walmart',
    disabled: true,
    validate: async () => { return ''}
}]

export const GetBrand = ({
    values, isFinished, onSubmit 
}: FormProps) => {
    const userName = values?.find((v) => v?.key === 'name')?.value || '';
    const [shopUrl, setShopUrl] = useState('');


    const [error, setError] = useState('');
    const [verifying, setVerifying] = useState(false);
    const {showToast} = useToast();
    const firstInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (firstInputRef.current) {
                firstInputRef.current.focus();
            }
        }, 900);

        return () => clearTimeout(timer);
    }, []);

    const handleSubmit = async () => {
        const error = validateShopUrl(shopUrl);
        setError(error || '');

        if(error){
            return;
        }

        setVerifying(true);
        try {
            const res = await authService.connectShopify({
                shop_domain: `${shopUrl}.myshopify.com`,
                redirect_url: window.location.href,
            })
            if(!res.authorizationUrl){
                setVerifying(false);
                setError('Shopify store is not found. Please check the store url.');
                return;
            }
            window.location.href = res.authorizationUrl;
        } catch (error) {
            console.error('创建品牌报告失败:', error);
            showToast(
                'Something went wrong. Please try again.',
                'error'
            );
            setVerifying(false)
        }
    }

    const extractShopifyDomain = useCallback((url: string): string | null => {
        try {
            // 移除开头的 @ 符号（如果有）
            const cleanUrl = url.startsWith('@') ? url.slice(1) : url;

            // 如果没有协议，添加 https://
            const urlWithProtocol = cleanUrl.startsWith('http') ? cleanUrl : `https://${cleanUrl}`;

            // 解析 URL
            const urlObj = new URL(urlWithProtocol);

            // 检查是否是 myshopify.com 域名
            if (!urlObj.hostname.endsWith('.myshopify.com')) {
                throw new Error('Invalid Shopify store URL');
            }

            // 提取店铺名
            return urlObj.hostname.split('.')[0];
        } catch (err) {
            return null;
        }
    }, []);

    const validateShopUrl = useCallback((input: string): string | null => {
        if (!input.trim()) {
            return 'Please enter your Shopify store url';
        }

        // 如果输入包含完整URL，先提取店铺名进行校验
        let shopName = input;
        if (input.includes('.myshopify.com')) {
            const domain = extractShopifyDomain(input);
            if (!domain) {
                return 'Please enter a valid Shopify store URL (e.g., yourstore.myshopify.com)';
            }
            shopName = domain;
        }

        // 校验店铺名格式
        if (shopName.length < 3) {
            return 'Store name must be at least 3 characters long';
        }

        // 校验店铺名只包含字母、数字和连字符
        if (!/^[a-zA-Z0-9-]+$/.test(shopName)) {
            return 'Store name can only contain letters, numbers, and hyphens';
        }

        // 校验不能以连字符开头或结尾
        if (shopName.startsWith('-') || shopName.endsWith('-')) {
            return 'Store name cannot start or end with a hyphen';
        }

        return null;
    }, [extractShopifyDomain]);


    const handleInputChange = useCallback((value: string) => {
        // 检查是否粘贴了完整的 Shopify URL，如果是则自动提取店铺名
        let processedValue = value;
        if (value.includes('.myshopify.com')) {
            const extractedDomain = extractShopifyDomain(value);
            if (extractedDomain) {
                processedValue = extractedDomain;
            }
        }
        
        setShopUrl(processedValue);
        setError('');
        
        // Real-time validation
        if (processedValue.trim()) {
            const validationErr = validateShopUrl(processedValue);
            if (validationErr) {
                setError(validationErr);
            }
        }
    }, [validateShopUrl, extractShopifyDomain]);
    
    return (
        <>
            <div className={styles.message}>
                <AnimatedElement delay={200}>
                    <p>
                        <TypewriterText 
                            text={`Lovely to meet you, ${userName}. Please connect your store to personalize your Bustly experience. `}
                            delay={200}
                            step={2}
                        />
                        <br />
                        <TypewriterText 
                            text={`Don't worry, you can always change these details later.`}
                            delay={1200}
                            step={2}
                        />
                    </p>
                </AnimatedElement>
                <AnimatedElement delay={1600} className={styles.inputGroup}>
                    <div style={{maxWidth: 520}}>
                        <ShopifyUrlInput
                            ref={firstInputRef}
                            value={shopUrl}
                            showIcon={true}
                            onChange={handleInputChange}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSubmit();
                                }
                            }}
                            error={error}
                        />
                    </div>
                    <Button style={{width: '174px', marginTop: 6}} loading={verifying} onClick={handleSubmit}>Connect</Button>
                    <div className={styles.comming}>
                        <p>Comming Soon</p>
                        <img src={CommingImg}></img>
                    </div>
                </AnimatedElement>
            </div>
            {isFinished ? (
                <div className={styles.userMessage}>
                    {shopUrl}
                </div>
            ) : null}
        </>
    );
}

export default GetBrand;